import { convertToPln } from '@apka/core'
import type { FxRate, IsoDate, PaymentView } from '@apka/core'
import { useLiveQuery } from 'dexie-react-hooks'
import { db } from './db.js'
import type { Dostawca, Kategoria, Klient, Platnosc, Pozycja } from './typy.js'

/**
 * Buduje `PaymentView` dokładnie tą samą regułą co API: zapłacona płatność
 * niesie zamrożoną złotówkę, planowana jest przeliczana po kursie z dnia
 * terminu. Dzięki temu licznik na telefonie pokazuje to samo co serwer,
 * również bez sieci.
 */
export function zbudujWidoki(
  platnosci: readonly Platnosc[],
  pozycje: ReadonlyMap<string, Pozycja>,
  kursy: readonly FxRate[],
): PaymentView[] {
  const widoki: PaymentView[] = []

  for (const platnosc of platnosci) {
    const pozycja = pozycje.get(platnosc.itemId)
    if (pozycja === undefined) continue

    if (platnosc.amountPlnMinor !== null) {
      widoki.push({
        id: platnosc.id,
        itemId: platnosc.itemId,
        cadence: pozycja.cadence,
        type: pozycja.type,
        status: platnosc.status,
        dueDate: platnosc.dueDate,
        amountPlnMinor: platnosc.amountPlnMinor,
        categoryId: pozycja.categoryId,
        isEstimate: false,
      })
      continue
    }

    const przeliczone = convertToPln(
      platnosc.plannedAmountMinor,
      platnosc.plannedCurrency,
      platnosc.dueDate,
      kursy,
    )
    widoki.push({
      id: platnosc.id,
      itemId: platnosc.itemId,
      cadence: pozycja.cadence,
      type: pozycja.type,
      status: platnosc.status,
      dueDate: platnosc.dueDate,
      amountPlnMinor: przeliczone?.amountPlnMinor ?? 0,
      categoryId: pozycja.categoryId,
      isEstimate: true,
    })
  }

  return widoki
}

/** Zwraca `undefined`, dopóki Dexie nie odda pierwszego wyniku — to sygnał do skeletonu. */
export function uzyjWidokowPlatnosci(od: IsoDate, doDnia: IsoDate): PaymentView[] | undefined {
  return useLiveQuery(async () => {
    const [platnosci, pozycje, kursy] = await Promise.all([
      db.payments.where('dueDate').between(od, doDnia, true, true).toArray(),
      db.items.toArray(),
      db.fxRates.toArray(),
    ])
    const mapa = new Map(pozycje.map((pozycja) => [pozycja.id, pozycja]))
    return zbudujWidoki(platnosci, mapa, kursy)
  }, [od, doDnia])
}

export function uzyjPlatnosci(od: IsoDate, doDnia: IsoDate): Platnosc[] | undefined {
  return useLiveQuery(
    () => db.payments.where('dueDate').between(od, doDnia, true, true).sortBy('dueDate'),
    [od, doDnia],
  )
}

export function uzyjPozycje(): Pozycja[] | undefined {
  return useLiveQuery(() => db.items.orderBy('title').toArray(), [])
}

export function uzyjPozycjeWgId(): Map<string, Pozycja> | undefined {
  return useLiveQuery(async () => {
    const pozycje = await db.items.toArray()
    return new Map(pozycje.map((pozycja) => [pozycja.id, pozycja]))
  }, [])
}

export function uzyjKategorie(): Kategoria[] | undefined {
  return useLiveQuery(() => db.categories.orderBy('name').toArray(), [])
}

export function uzyjKategorieWgId(): Map<string, Kategoria> | undefined {
  return useLiveQuery(async () => {
    const kategorie = await db.categories.toArray()
    return new Map(kategorie.map((kategoria) => [kategoria.id, kategoria]))
  }, [])
}

export function uzyjDostawcow(): Dostawca[] | undefined {
  return useLiveQuery(() => db.vendors.orderBy('name').toArray(), [])
}

export function uzyjKlientow(): Klient[] | undefined {
  return useLiveQuery(() => db.clients.orderBy('name').toArray(), [])
}

/**
 * Waluty, których nie da się przeliczyć w danym zakresie — brak notowania
 * NBP oznacza, że część kwot wchodzi do liczników jako zero. Interfejs musi
 * to powiedzieć wprost, bo samo „0,00 zł" wygląda na zepsutą aplikację.
 */
export function uzyjWalutBezKursu(od: IsoDate, doDnia: IsoDate): string[] | undefined {
  return useLiveQuery(async () => {
    const [platnosci, kursy] = await Promise.all([
      db.payments.where('dueDate').between(od, doDnia, true, true).toArray(),
      db.fxRates.toArray(),
    ])

    const braki = new Set<string>()
    for (const platnosc of platnosci) {
      if (platnosc.amountPlnMinor !== null) continue
      if (platnosc.plannedCurrency === 'PLN') continue
      const przeliczone = convertToPln(
        platnosc.plannedAmountMinor,
        platnosc.plannedCurrency,
        platnosc.dueDate,
        kursy,
      )
      if (przeliczone === null) braki.add(platnosc.plannedCurrency)
    }
    return [...braki].sort()
  }, [od, doDnia])
}
